import { Link } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEO, { breadcrumbJsonLd } from '../../components/SEO';

/** Industry pill colors for case study cards */
function industryBadgeClass(industry: string): string {
  const c = industry.toLowerCase();
  if (c.includes('restaurant') || c.includes('qsr')) return 'bg-orange-50 text-orange-700 border border-orange-100';
  if (c.includes('pharma') || c.includes('health')) return 'bg-teal-50 text-teal-700 border border-teal-100';
  if (c.includes('grocery') || c.includes('commerce')) return 'bg-emerald-50 text-emerald-700 border border-emerald-100';
  if (c.includes('e-commerce') || c.includes('retail')) return 'bg-rose-50 text-rose-700 border border-rose-100';
  if (c.includes('courier') || c.includes('parcel') || c.includes('logistics'))
    return 'bg-sky-50 text-sky-700 border border-sky-100';
  if (c.includes('3pl') || c.includes('contract')) return 'bg-violet-50 text-violet-700 border border-violet-100';
  return 'bg-blue-50 text-blue-700 border border-blue-100';
}

/** One section of narrative (heading + paragraphs) */
export interface CaseStudyNarrativeSection {
  heading?: string;
  paragraphs: string[];
}

/** Infographic block: image + optional title/caption */
export interface CaseStudyInfographic {
  image: string;
  alt: string;
  title?: string;
  caption?: string;
}

/** Before/after or stat block for analytics */
export interface CaseStudyAnalytics {
  title: string;
  subtitle?: string;
  beforeAfter?: { label: string; before: string; after: string }[];
  stats?: { value: string; label: string }[];
  chartLabel?: string;
}

/** Standalone image with optional caption */
export interface CaseStudyImage {
  src: string;
  alt: string;
  caption?: string;
}

/** Timeline phase for implementation */
export interface CaseStudyTimelinePhase {
  phase: string;
  duration: string;
  description: string;
}

/** Full case study content for detail page */
export interface CaseStudyFullContent {
  narrative: CaseStudyNarrativeSection[];
  infographics: CaseStudyInfographic[];
  analytics: CaseStudyAnalytics[];
  images: CaseStudyImage[];
  keyTakeaways: string[];
  timeline?: CaseStudyTimelinePhase[];
}

/** Concise SERP metadata for the detail page (title ≤~60 chars, description 150–160) */
export interface CaseStudySEO {
  documentTitle: string;
  metaDescription: string;
}

export interface CaseStudyEntry {
  /** URL slug for detail page, e.g. "qsr-route-optimization" */
  slug: string;
  /** Anonymous descriptor, e.g. "Leading QSR chain" */
  company: string;
  industry: string;
  /** Short topical tags (use case, capability, region) shown as chips on cards and detail */
  tags?: string[];
  headline: string;
  challenge: string;
  solution: string;
  metrics: { value: string; label: string }[];
  quote: string;
  /** Anonymous attribution, e.g. "Head of Operations" */
  author: string;
  role: string;
  /** Hero image path for the story card */
  heroImage?: string;
  /** Per-study SEO title + meta description; falls back to headline/challenge when omitted */
  seo?: CaseStudySEO;
  /** Optional FAQ — rendered on the detail page and emitted as FAQPage structured data */
  faq?: { question: string; answer: string }[];
  /** Optional full content for the case study detail page */
  fullContent?: CaseStudyFullContent;
}

export interface ResourceData {
  slug: string;
  icon: string;
  name: string;
  tagline: string;
  headline: string;
  headlineHighlight: string;
  description: string;
  heroStats: { value: string; label: string }[];
  howItWorks: { step: string; title: string; description: string; icon: string }[];
  capabilities: { icon: string; title: string; description: string; tag?: string }[];
  benefits: { value: string; label: string; description: string }[];
  testimonial?: { quote: string; author: string; role: string; company: string; metric: string };
  faq: { question: string; answer: string }[];
  /** Optional list of case studies (for case-studies page) */
  caseStudies?: CaseStudyEntry[];
}

export default function ResourcePage({ data }: { data: ResourceData }) {
  const { t } = useTranslation();
  const isCaseStudies = data.slug === 'case-studies' && Boolean(data.caseStudies?.length);
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const storiesRef = useRef<HTMLDivElement>(null);

  function selectIndustry(industry: string) {
    setSelectedIndustry(industry);
  }

  useEffect(() => {
    if (!isCaseStudies || selectedIndustry === 'all') return;
    storiesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [isCaseStudies, selectedIndustry]);

  const uniqueIndustries = useMemo(
    () =>
      Array.from(new Set((data.caseStudies ?? []).map((s) => s.industry))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [data.caseStudies]
  );

  const filteredCaseStudies = useMemo(() => {
    const list = data.caseStudies ?? [];
    if (selectedIndustry === 'all') return list;
    return list.filter((s) => s.industry === selectedIndustry);
  }, [data.caseStudies, selectedIndustry]);

  const showFeatured = isCaseStudies && selectedIndustry === 'all' && filteredCaseStudies.length > 0;
  const featuredStudy = showFeatured ? filteredCaseStudies[0] : undefined;
  const gridStudies = featuredStudy ? filteredCaseStudies.slice(1) : filteredCaseStudies;

  return (
    <div className="pt-24">
      <SEO
        title={`${data.headline} ${data.headlineHighlight}`}
        description={data.description}
        jsonLd={breadcrumbJsonLd([
          { name: t('common.breadcrumb.home'), url: 'https://geofleet.ai' },
          { name: t('common.breadcrumb.resources'), url: 'https://geofleet.ai/resources' },
          {
            name: isCaseStudies ? t('common.breadcrumb.caseStudies') : data.headline,
            url: `https://geofleet.ai/resources/${data.slug}`,
          },
        ])}
      />

      {/* Hero */}
      {isCaseStudies ? (
        <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-blue-50/40 to-white pb-6 lg:pb-8">
          <div
            className="pointer-events-none absolute right-0 top-0 h-[min(420px,80vw)] w-[min(520px,90vw)] translate-x-1/4 -translate-y-1/3 rounded-full bg-blue-500/[0.08] blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute bottom-0 left-0 h-[min(320px,60vw)] w-[min(400px,80vw)] -translate-x-1/3 translate-y-1/4 rounded-full bg-emerald-500/[0.06] blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute left-1/2 top-1/3 h-48 w-72 -translate-x-1/2 rounded-full bg-violet-500/[0.04] blur-3xl"
            aria-hidden
          />

          <div className="relative z-10 mx-auto max-w-7xl px-6 pt-10 lg:pt-14">
            <div className="mx-auto max-w-3xl text-center">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-100/90 bg-white/90 px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm backdrop-blur-sm">
                <i className={data.icon} aria-hidden />
                <span>{t('caseStudies.page.tagline')}</span>
              </div>
              <h1 className="text-balance text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12]">
                {t('caseStudies.page.headline')}{' '}
                <span className="bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
                  {t('caseStudies.page.headlineHighlight')}
                </span>
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-gray-600 sm:text-lg">
                {t('caseStudies.page.description')}
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <a
                  href="https://app.geofleet.ai/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-blue-500/25 transition-colors hover:bg-blue-700"
                >
                  {t('common.ctaStartTrial')}
                  <i className="ri-arrow-right-line text-sm" aria-hidden />
                </a>
                <a
                  href="https://meetings.hubspot.com/aditya145"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-blue-200 hover:text-blue-700"
                >
                  <i className="ri-calendar-line" aria-hidden />
                  {t('common.ctaBookDemo')}
                </a>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="bg-white py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-6">
            <div className="mx-auto max-w-3xl text-center">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#eef5ff] px-3.5 py-1.5 text-xs font-semibold text-[#1769ff]">
                <i className={data.icon} aria-hidden />
                <span>{data.tagline}</span>
              </div>
              <h1 className="mb-5 text-3xl font-bold leading-tight text-gray-900 lg:text-[2.75rem]">
                {data.headline}{' '}
                <span className="text-[#1769ff]">{data.headlineHighlight}</span>
              </h1>
              <p className="mx-auto mb-8 max-w-2xl text-base leading-relaxed text-gray-600">{data.description}</p>
              <div className="mb-10 flex flex-wrap justify-center gap-3">
                <a
                  href="https://app.geofleet.ai/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-[#1769ff]/15 transition-all hover:opacity-90"
                  style={{ backgroundColor: '#1769ff' }}
                >
                  <span>{t('common.ctaStartTrial')}</span>
                  <i className="ri-arrow-right-line text-sm" aria-hidden />
                </a>
                <a
                  href="https://meetings.hubspot.com/aditya145"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-all hover:border-[#1769ff] hover:text-[#1769ff]"
                >
                  <i className="ri-calendar-line" aria-hidden />
                  <span>{t('common.ctaBookDemo')}</span>
                </a>
              </div>
              <div className="flex flex-wrap justify-center gap-10">
                {data.heroStats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-2xl font-bold text-[#1769ff]">{stat.value}</div>
                    <div className="mt-0.5 text-xs text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Case studies — featured + grid (shown first on case studies page) */}
      {isCaseStudies && (
        <section ref={storiesRef} className="relative z-20 scroll-mt-28 pb-8 lg:pb-12">
          <div className="mx-auto max-w-7xl px-6">
            <IndustryFilterBar
              industries={uniqueIndustries}
              selected={selectedIndustry}
              onSelect={selectIndustry}
            />

            {featuredStudy && (
              <>
                <div className="mb-4 flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100">
                    <i className="ri-star-fill text-sm text-amber-600" aria-hidden />
                  </div>
                  <span className="text-sm font-bold uppercase tracking-wider text-gray-900">
                    {t('caseStudies.featured.label')}
                  </span>
                </div>
                <FeaturedCaseStudyCard study={featuredStudy} />
              </>
            )}

            <div className={`flex items-end justify-between gap-4 ${featuredStudy ? 'mt-12' : 'mt-2'} mb-8 lg:mb-10`}>
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-100">
                    <i className="ri-team-line text-lg text-blue-600" aria-hidden />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedIndustry === 'all' ? t('caseStudies.list.title') : selectedIndustry}
                  </h2>
                </div>
                <p className="text-sm text-gray-500">
                  {selectedIndustry === 'all'
                    ? t('caseStudies.list.defaultSubtitle')
                    : t('caseStudies.list.countInIndustry', { count: filteredCaseStudies.length })}
                </p>
              </div>
              <span className="shrink-0 rounded-full border border-gray-200 bg-white px-3 py-1 text-sm font-medium text-gray-600 shadow-sm">
                {t('caseStudies.list.story', { count: filteredCaseStudies.length })}
              </span>
            </div>

            {gridStudies.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-16 px-6 text-center shadow-sm">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100">
                  <i className="ri-filter-off-line text-2xl text-gray-400" aria-hidden />
                </div>
                <p className="mb-1 text-base font-medium text-gray-900">{t('caseStudies.empty.title')}</p>
                <p className="text-sm text-gray-600">{t('caseStudies.empty.hint')}</p>
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 sm:gap-7 lg:grid-cols-3 lg:gap-8">
                {gridStudies.map((study) => (
                  <CaseStudyCard key={study.slug} study={study} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* How it works */}
      <section className={isCaseStudies ? 'border-t border-gray-100 bg-slate-50/70 py-14 lg:py-16' : 'bg-[#FAFAFA] py-16'}>
        <div className="mx-auto max-w-5xl px-6">
          <div className="mb-10 text-center lg:mb-12">
            {isCaseStudies && (
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 shadow-sm ring-1 ring-gray-200">
                <i className="ri-route-line text-blue-600" aria-hidden />
                {t('caseStudies.explore.badge')}
              </div>
            )}
            <h2 className="mb-3 text-2xl font-bold text-gray-900 lg:text-3xl">{t('resources.howItWorks.title')}</h2>
            <p className="text-base text-gray-600">{t('resources.howItWorks.subtitle')}</p>
          </div>
          <div className="grid gap-5 md:grid-cols-3 md:gap-6">
            {data.howItWorks.map((step, i) => (
              <div
                key={i}
                className={`relative rounded-2xl border p-6 text-center transition-shadow hover:shadow-md ${
                  isCaseStudies
                    ? 'border-gray-200/80 bg-white shadow-sm'
                    : 'border-gray-100 bg-white'
                }`}
              >
                <div className="absolute -top-3 left-1/2 flex h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white shadow-sm">
                  {step.step}
                </div>
                <div className="mx-auto mb-4 mt-3 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50">
                  <i className={`${step.icon} text-xl text-blue-600`} aria-hidden />
                </div>
                <h3 className="mb-2 text-base font-bold text-gray-900">{step.title}</h3>
                <p className="text-sm leading-relaxed text-gray-500">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities / What's inside */}
      <section className={`py-14 lg:py-16 ${isCaseStudies ? 'bg-white' : 'bg-white'}`}>
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-10 text-center lg:mb-12">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3.5 py-1.5 text-xs font-semibold text-blue-700">
              <i className="ri-sparkling-2-line" aria-hidden />
              <span>{t('resources.whatsInside.badge')}</span>
            </div>
            <h2 className="mb-3 text-2xl font-bold text-gray-900 lg:text-3xl">
              {t('resources.whatsInside.title', { name: data.name })}
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {data.capabilities.map((cap, i) => (
              <div
                key={i}
                className="group relative cursor-pointer rounded-2xl border border-gray-100 bg-slate-50/50 p-6 transition-all hover:border-blue-200/60 hover:bg-white hover:shadow-lg hover:shadow-blue-100/30"
              >
                {cap.tag && (
                  <span className="absolute right-4 top-4 rounded-full bg-blue-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-blue-600">
                    {cap.tag}
                  </span>
                )}
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 transition-colors group-hover:bg-blue-600">
                  <i className={`${cap.icon} text-lg text-blue-600 transition-colors group-hover:text-white`} aria-hidden />
                </div>
                <h3 className="mb-1.5 text-sm font-bold text-gray-900">{cap.title}</h3>
                <p className="text-xs leading-relaxed text-gray-500">{cap.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits / Metrics */}
      <section className="relative overflow-hidden bg-gray-900 py-14 lg:py-16">
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-emerald-500/5"
          aria-hidden
        />
        <div className="relative mx-auto max-w-5xl px-6">
          <div className="mb-10 text-center lg:mb-12">
            <h2 className="mb-3 text-2xl font-bold text-white lg:text-3xl">{t('resources.whyItMatters.title')}</h2>
            <p className="text-base text-gray-400">{t('resources.whyItMatters.subtitle', { name: data.name })}</p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
            {data.benefits.map((b, i) => (
              <div
                key={i}
                className="rounded-2xl border border-gray-700/50 bg-gray-800/50 p-6 text-center transition-colors hover:border-blue-500/30 hover:bg-gray-800/80"
              >
                <div className="mb-2 text-3xl font-bold text-blue-400">{b.value}</div>
                <div className="mb-1 text-sm font-semibold text-white">{b.label}</div>
                <p className="text-xs leading-relaxed text-gray-400">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {data.testimonial && (
      <section className={`py-14 lg:py-16 ${isCaseStudies ? 'bg-slate-50/70' : 'bg-[#FAFAFA]'}`}>
        <div className="mx-auto max-w-3xl px-6">
          <div className="rounded-2xl border border-gray-200/80 bg-white p-8 text-center shadow-lg shadow-gray-200/50 ring-1 ring-black/[0.03] lg:p-10">
            <span className="mb-5 inline-block rounded-full bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-blue-600">
              {data.testimonial.metric}
            </span>
            <div className="mb-4 flex justify-center gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <i key={s} className="ri-star-fill text-[#FFB800]" aria-hidden />
              ))}
            </div>
            <blockquote className="mx-auto mb-6 max-w-xl text-lg font-medium leading-relaxed text-gray-800">
              &ldquo;{data.testimonial.quote}&rdquo;
            </blockquote>
            <div className="text-sm font-bold text-gray-900">{data.testimonial.author}</div>
            <div className="text-xs text-gray-500">
              {data.testimonial.role}, {data.testimonial.company}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* FAQ */}
      <section className={`py-14 lg:py-16 ${isCaseStudies ? 'bg-white' : 'bg-[#FAFAFA]'}`}>
        <div className="mx-auto max-w-3xl px-6">
          <h2 className="mb-10 text-center text-2xl font-bold text-gray-900">{t('common.faqTitle')}</h2>
          <div className="space-y-3">
            {data.faq.map((item, i) => (
              <details
                key={i}
                className="group rounded-xl border border-gray-200/80 bg-white shadow-sm transition-shadow open:shadow-md"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between p-5 text-sm font-semibold text-gray-900">
                  <span className="pr-4">{item.question}</span>
                  <i className="ri-add-line flex-shrink-0 text-gray-400 group-open:hidden" aria-hidden />
                  <i className="ri-subtract-line hidden flex-shrink-0 text-blue-600 group-open:block" aria-hidden />
                </summary>
                <div className="-mt-1 px-5 pb-5">
                  <p className="text-sm leading-relaxed text-gray-600">{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-600 py-14 lg:py-16">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="mb-3 text-2xl font-bold text-white lg:text-3xl">
            {isCaseStudies
              ? t('caseStudies.cta.title')
              : t('resources.cta.exploreTitle', { name: data.name })}
          </h2>
          <p className="mb-7 text-base text-white/80">
            {isCaseStudies ? t('caseStudies.cta.subtitle') : t('resources.cta.exploreSubtitle')}
          </p>
          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="https://app.geofleet.ai/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-xl bg-white px-6 py-2.5 text-sm font-semibold text-blue-600 shadow-md transition-all hover:bg-gray-100"
            >
              {t('common.ctaStartTrial')}
            </a>
            <a
              href="https://meetings.hubspot.com/aditya145"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-xl border-2 border-white/80 px-6 py-2.5 text-sm font-medium text-white transition-all hover:bg-white/10"
            >
              {t('common.ctaBookDemo')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function IndustryFilterBar({
  industries,
  selected,
  onSelect,
}: {
  industries: string[];
  selected: string;
  onSelect: (industry: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="relative z-20 mb-8 rounded-2xl border border-gray-200/80 bg-white p-4 shadow-sm ring-1 ring-gray-100 sm:p-5">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
        {t('caseStudies.filters.label')}
      </p>
      <div
        className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        role="group"
        aria-label={t('caseStudies.filters.ariaLabel')}
      >
        <button
          type="button"
          onClick={() => onSelect('all')}
          aria-pressed={selected === 'all'}
          className={`relative z-20 shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${
            selected === 'all'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'border border-gray-200 bg-white text-gray-600 hover:border-blue-200 hover:text-blue-700'
          }`}
        >
          {t('caseStudies.filters.allIndustries')}
        </button>
        {industries.map((industry) => (
          <button
            key={industry}
            type="button"
            onClick={() => onSelect(industry)}
            aria-pressed={selected === industry}
            className={`relative z-20 shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${
              selected === industry
                ? 'bg-blue-600 text-white shadow-sm'
                : 'border border-gray-200 bg-white text-gray-600 hover:border-blue-200 hover:text-blue-700'
            }`}
          >
            {industry}
          </button>
        ))}
      </div>
    </div>
  );
}

function FeaturedCaseStudyCard({ study }: { study: CaseStudyEntry }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/resources/case-studies/${study.slug}`}
      className="group grid grid-cols-1 items-stretch overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-xl shadow-gray-200/60 ring-1 ring-black/[0.03] transition-all duration-300 hover:border-blue-200/80 hover:shadow-2xl hover:shadow-blue-100/50 lg:grid-cols-5"
    >
      {study.heroImage && (
        <div className="relative aspect-[16/10] overflow-hidden bg-gray-900 lg:col-span-3 lg:aspect-auto lg:min-h-[340px]">
          <img
            src={study.heroImage}
            alt=""
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-gray-900/60 via-gray-900/10 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-gray-900/20" />
          <div className="absolute bottom-4 left-4 flex flex-wrap gap-2 lg:hidden">
            {study.metrics.slice(0, 2).map((m) => (
              <span
                key={m.label}
                className="rounded-lg bg-white/95 px-3 py-1.5 text-xs font-bold text-blue-600 shadow-sm backdrop-blur-sm"
              >
                {m.value} {m.label}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="flex min-w-0 flex-col justify-center p-6 sm:p-8 lg:col-span-2 lg:p-10">
        <span
          className={`mb-4 inline-flex self-start rounded-md px-2.5 py-1 text-xs font-semibold ${industryBadgeClass(study.industry)}`}
        >
          {study.industry}
        </span>
        <h3 className="mb-2 text-2xl font-bold leading-tight text-gray-900 transition-colors group-hover:text-blue-600 lg:text-[1.65rem]">
          {study.headline}
        </h3>
        <p className="mb-1 text-sm font-medium text-gray-500">{study.company}</p>
        <p className="mb-5 line-clamp-3 text-base leading-relaxed text-gray-600">{study.challenge}</p>
        <div className="mb-5 hidden flex-wrap gap-6 lg:flex">
          {study.metrics.map((m) => (
            <div key={m.label}>
              <div className="text-2xl font-bold text-blue-600">{m.value}</div>
              <div className="text-[11px] text-gray-500">{m.label}</div>
            </div>
          ))}
        </div>
        <blockquote className="mb-5 border-l-2 border-blue-200 pl-4 text-sm italic leading-relaxed text-gray-600 line-clamp-2">
          &ldquo;{study.quote}&rdquo;
        </blockquote>
        <p className="mb-5 text-xs text-gray-500">
          {study.author} · {study.role}
        </p>
        <span className="inline-flex items-center gap-2 self-start rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all group-hover:bg-blue-700 group-hover:gap-3">
          {t('caseStudies.card.readFullStory')} <i className="ri-arrow-right-line" aria-hidden />
        </span>
      </div>
    </Link>
  );
}

function CaseStudyCard({ study }: { study: CaseStudyEntry }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/resources/case-studies/${study.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-200/80 hover:shadow-xl hover:shadow-blue-100/40"
    >
      {study.heroImage && (
        <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
          <img
            src={study.heroImage}
            alt=""
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-gray-900/50 to-transparent" />
          <span
            className={`absolute left-3 top-3 rounded-md px-2 py-1 text-[10px] font-semibold shadow-sm backdrop-blur-sm ${industryBadgeClass(study.industry)}`}
          >
            {study.industry}
          </span>
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        <h3 className="mb-1 line-clamp-2 text-base font-bold leading-snug text-gray-900 transition-colors group-hover:text-blue-600">
          {study.headline}
        </h3>
        <p className="mb-3 text-xs font-medium text-gray-500">{study.company}</p>
        <p className="mb-4 line-clamp-2 flex-1 text-sm leading-relaxed text-gray-600">{study.challenge}</p>
        <div className="mb-4 flex flex-wrap gap-4 border-t border-gray-100 pt-4">
          {study.metrics.slice(0, 3).map((m) => (
            <div key={m.label}>
              <div className="text-lg font-bold text-blue-600">{m.value}</div>
              <div className="text-[10px] text-gray-500">{m.label}</div>
            </div>
          ))}
        </div>
        <blockquote className="mb-3 line-clamp-2 text-xs italic leading-relaxed text-gray-500">
          &ldquo;{study.quote}&rdquo;
        </blockquote>
        <span className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-blue-600 transition-all group-hover:gap-2">
          {t('caseStudies.card.readFullStory')} <i className="ri-arrow-right-line" aria-hidden />
        </span>
      </div>
    </Link>
  );
}
